import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import { useSessionId } from '@/hooks/use-session-id';
import { supabase } from '@/integrations/supabase/client';

interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

const getUTMParams = (search: string): UTMParams => {
  const params = new URLSearchParams(search);
  const utmParams: UTMParams = {};
  
  const keys: (keyof UTMParams)[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  keys.forEach(key => {
    const value = params.get(key);
    if (value) utmParams[key] = value;
  });
  
  return utmParams;
};

export const usePageTracking = () => {
  const location = useLocation();
  const sessionId = useSessionId();
  const pageStartTime = useRef<number>(Date.now());
  const previousPath = useRef<string | null>(null);
  const hasTrackedInitialView = useRef(false);

  useEffect(() => {
    if (!sessionId) return;

    const trackPageView = async () => {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const utmParams = getUTMParams(location.search);
      const referrer = document.referrer || undefined;
      
      await trackEvent({
        type: 'page_view',
        category: 'navigation',
        sessionId,
        userId,
        metadata: {
          page_path: location.pathname,
          page_search: location.search || undefined,
          referrer: !hasTrackedInitialView.current ? referrer : undefined,
          previous_page: previousPath.current || undefined,
          ...utmParams,
        },
      });

      hasTrackedInitialView.current = true;
      previousPath.current = location.pathname;
      pageStartTime.current = Date.now();
    };

    // Track time spent on previous page before navigating
    const trackTimeOnPage = async () => {
      if (previousPath.current && previousPath.current !== location.pathname) {
        const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
        
        if (timeSpent > 1) { // Only track if spent more than 1 second
          const userId = (await supabase.auth.getUser()).data.user?.id;
          
          await trackEvent({
            type: 'page_view',
            category: 'navigation',
            sessionId,
            userId,
            metadata: {
              page_path: previousPath.current,
              time_spent_seconds: timeSpent,
              event_subtype: 'page_exit',
            },
          });
        }
      }
    };

    trackTimeOnPage().then(() => trackPageView());

    // Track time on page when user leaves
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
        const userId = (await supabase.auth.getUser()).data.user?.id;
        
        if (timeSpent > 1) {
          // Use sendBeacon for reliable delivery
          if (navigator.sendBeacon) {
            // For now, just track in the normal way since we don't have a beacon endpoint
            trackEvent({
              type: 'page_view',
              category: 'navigation',
              sessionId,
              userId,
              metadata: {
                page_path: location.pathname,
                time_spent_seconds: timeSpent,
                event_subtype: 'visibility_hidden',
              },
            });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, location.search, sessionId]);
};
